import { useState, useEffect, type FormEvent } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';

const navItems = [
  { label: '首页', to: '/' },
  { label: '电影', to: '/category/电影' },
  { label: '电视剧', to: '/category/电视剧' },
  { label: '综艺', to: '/category/综艺' },
  { label: '动漫', to: '/category/动漫' },
  { label: '资讯', to: '/news' },
];

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrollHidden, setScrollHidden] = useState(false);
  const [keyword, setKeyword] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      setScrollHidden(y > lastY && y > 160);
      lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    navigate(`/search?q=${encodeURIComponent(value)}`);
    setKeyword('');
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b border-background-200/80 bg-background-50/90 backdrop-blur-md transition-transform duration-300 ${
        scrollHidden ? '-translate-y-full md:translate-y-0' : 'translate-y-0'
      }`}
    >
      <div className="mx-auto flex h-16 w-full items-center gap-4 px-4 md:h-[68px] md:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2.5" aria-label="蜜桃网首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-primary-400 to-accent-500 text-background-50">
            <i className="ri-movie-2-line text-xl" />
          </span>
          <span className="font-heading text-xl font-bold tracking-tight text-foreground-950">
            蜜桃<span className="text-primary-600">网</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-md px-3.5 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <form onSubmit={handleSearch} className="ml-auto hidden w-full max-w-xs items-center lg:flex">
          <div className="relative w-full">
            <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400" />
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视、资讯"
              className="h-10 w-full rounded-full border border-background-200 bg-background-100 pl-9 pr-4 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors focus:border-primary-400 focus:bg-background-50"
            />
          </div>
        </form>

        <Link
          to="/search"
          className="ml-auto flex h-10 w-10 items-center justify-center rounded-full bg-background-100 text-foreground-800 transition-colors hover:bg-primary-100 hover:text-primary-700 lg:hidden"
          aria-label="搜索"
        >
          <i className="ri-search-line text-lg" />
        </Link>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-background-100 text-foreground-800 transition-colors hover:bg-background-200 md:hidden"
          aria-label="打开菜单"
        >
          <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200/80 bg-background-50 px-4 pb-4 pt-2 md:hidden">
          <nav className="flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-foreground-700 hover:bg-background-100'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}